import React from "react";
import {
  Form,
  InputGroup,
  Button,
  Badge,
  Table,
  Row,
  Col,
} from "react-bootstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { AvailableProduct } from "../../types";

// Types
export interface FieldSchema {
  name: string;
  label: string;
  type:
  | "text"
  | "select"
  | "date"
  | "time"
  | "datetime-local"
  | "textarea"
  | "file"
  | "currency"
  | "tel"
  | "quantity"
  | "badge"
  | "checkbox"
  | "tiers"
  | "productList"
  | "inlineProductList";
  icon?: string;
  options?: string[];
  style?: "teal" | "dark";
  defaultValue?: string | number | boolean;
  accept?: string;
  hint?: string;
  prefix?: string;
  readOnly?: boolean;
  computed?: string;
  rows?: number;
  placeholder?: string;
}

interface FieldRendererProps {
  field: FieldSchema;
  value: any;
  formData: Record<string, any>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  onShowProductModal: () => void;
  availableProducts?: AvailableProduct[];
  /** When true, renders all inputs as disabled/greyed out for view-only mode */
  readOnly?: boolean;
  /** Called when user clicks to preview a base64 image in view mode */
  onShowImagePreview?: (imageSrc: string) => void;
  /** When true, supplier is editing: allow quantity changes & delete but hide add product button */
  supplierEditMode?: boolean;
}

const InlineProductListInput: React.FC<{
  field: FieldSchema;
  value: any;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  availableProducts: AvailableProduct[];
  readOnly?: boolean;
}> = ({ field, value, setFormData, availableProducts, readOnly }) => {
  const items = value || [];

  const handleAddRow = () => {
    const newRow = { product: "", quantity: "", reason: "", expiry: "" };
    setFormData((prev) => ({ ...prev, [field.name]: [...items, newRow] }));
  };

  const handleRemoveRow = (index: number) => {
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    setFormData((prev) => ({ ...prev, [field.name]: updatedItems }));
  };

  const handleItemChange = (
    index: number,
    fieldName: string,
    newValue: string
  ) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [fieldName]: newValue };
    setFormData((prev) => ({ ...prev, [field.name]: updatedItems }));
  };

  return (
    <div>
      {/* Header Row with Labels + Add Button */}
      <Row className="mb-2 g-2">
        <Col md={5}>
          <Form.Label className="small text-dark fw-semibold mb-0">
            Product
          </Form.Label>
        </Col>
        <Col md={1}>
          <Form.Label className="small text-dark fw-semibold mb-0">
            Quantity
          </Form.Label>
        </Col>
        <Col md={3}>
          <Form.Label className="small text-dark fw-semibold mb-0">
            Reason
          </Form.Label>
        </Col>
        <Col md={2}>
          <Form.Label className="small text-dark fw-semibold mb-0">
            Expiry Date
          </Form.Label>
        </Col>
        <Col md={1}>
          {!readOnly && (
            <Button
              variant="success"
              size="sm"
              onClick={handleAddRow}
              className="w-100"
            >
              <i className="bi bi-plus-lg"></i>
            </Button>
          )}
        </Col>
      </Row>

      {/* Data Rows */}
      {items.map((item: any, idx: number) => (
        <Row key={idx} className="mb-2 g-2 align-items-center">
          <Col md={5}>
            <Form.Select
              size="sm"
              value={item.product}
              onChange={(e) => handleItemChange(idx, "product", e.target.value)}
              disabled={readOnly}
            >
              <option value="">Select Product</option>
              {availableProducts.map((p, i) => (
                <option key={i} value={`${p.code} - ${p.name}`}>
                  {p.code} - {p.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={1}>
            <Form.Control
              size="sm"
              value={item.quantity}
              onChange={(e) =>
                handleItemChange(idx, "quantity", e.target.value)
              }
              placeholder="Qty"
              disabled={readOnly}
            />
          </Col>
          <Col md={3}>
            <Form.Control
              size="sm"
              value={item.reason}
              onChange={(e) => handleItemChange(idx, "reason", e.target.value)}
              placeholder="Reason"
              disabled={readOnly}
            />
          </Col>
          <Col md={2}>
            <Form.Control
              size="sm"
              value={item.expiry}
              onChange={(e) => handleItemChange(idx, "expiry", e.target.value)}
              placeholder="Expiry"
              disabled={readOnly}
            />
          </Col>
          <Col md={1}>
            {!readOnly && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleRemoveRow(idx)}
                className="w-100"
              >
                <i className="bi bi-x-lg"></i>
              </Button>
            )}
          </Col>
        </Row>
      ))}
    </div>
  );
};

const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  formData,
  setFormData,
  onShowProductModal,
  availableProducts = [],
  readOnly = false,
  onShowImagePreview,
  supplierEditMode = false,
}) => {
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value: inputValue } = e.target;
    setFormData((prev) => ({ ...prev, [name]: inputValue }));
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setFormData((prev) => ({ ...prev, [fieldName]: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileClear = (fieldName: string) => {
    setFormData((prev) => ({ ...prev, [fieldName]: null }));
  };

  const handleQuantityChange = (fieldName: string, delta: number) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: Math.max(1, (prev[fieldName] || 1) + delta),
    }));
  };

  const removeProduct = (fieldName: string, index: number) => {
    const newProducts = [...(formData[fieldName] || [])];
    newProducts.splice(index, 1);
    setFormData((prev) => ({ ...prev, [fieldName]: newProducts }));
  };

  switch (field.type) {
    case "badge":
      return (
        <Badge bg="secondary" className="fw-normal small px-2 py-1">
          {value || field.defaultValue}
        </Badge>
      );

    case "select":
      return (
        <InputGroup>
          {field.icon && (
            <InputGroup.Text className="input-icon">
              <i className={`bi ${field.icon}`}></i>
            </InputGroup.Text>
          )}
          <Form.Select
            name={field.name}
            value={value}
            onChange={handleInputChange}
            className={
              field.style === "teal" && !readOnly ? "form-select-dark small" : "small"
            }
            disabled={readOnly}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Form.Select>
        </InputGroup>
      );

    case "date":
      return (
        <InputGroup>
          {field.icon && (
            <InputGroup.Text className="input-icon">
              <i className={`bi ${field.icon}`}></i>
            </InputGroup.Text>
          )}
          <Flatpickr
            value={value}
            onChange={(dates: Date[]) => {
              // Store as YYYY-MM-DD to avoid UTC timezone shift (matches client format)
              if (dates[0]) {
                const d = dates[0];
                const pad = (n: number) => n.toString().padStart(2, "0");
                const localDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
                setFormData((prev) => ({ ...prev, [field.name]: localDate }));
              } else {
                setFormData((prev) => ({ ...prev, [field.name]: "" }));
              }
            }}
            options={{ dateFormat: "Y-m-d", altInput: true, altFormat: "d/m/Y", allowInput: !readOnly, clickOpens: !readOnly }}
            className="form-control small"
            placeholder="Select date"
            disabled={readOnly}
          />
        </InputGroup>
      );

    case "time":
      return (
        <InputGroup>
          {field.icon && (
            <InputGroup.Text className="input-icon">
              <i className={`bi ${field.icon}`}></i>
            </InputGroup.Text>
          )}
          <Flatpickr
            value={value}
            onChange={(dates: Date[]) => {
              const timeStr = dates[0]
                ? dates[0].toLocaleTimeString("en-GB", {
                  hour: "2-digit",
                  minute: "2-digit",
                })
                : "";
              setFormData((prev) => ({ ...prev, [field.name]: timeStr }));
            }}
            options={{
              enableTime: true,
              noCalendar: true,
              dateFormat: "H:i",
              time_24hr: true,
              clickOpens: !readOnly,
            }}
            className="form-control small"
            placeholder="Select time"
            disabled={readOnly}
          />
        </InputGroup>
      );

    case "textarea":
      const lineCount = Math.max(3, (value || "").split("\n").length);
      return (
        <div className="w-100">
          <div className="d-flex">
            {field.icon && (
              <div className="d-flex align-items-center pe-3">
                <i
                  className="bi bi-quote text-secondary"
                  style={{ fontSize: "1.25rem" }}
                ></i>
              </div>
            )}
            <div
              className="flex-grow-1 border rounded bg-white p-2"
              style={{ maxHeight: "120px", overflowY: "auto" }}
            >
              {Array.from({ length: lineCount }).map((_, idx) => {
                const lines = (value || "").split("\n");
                return (
                  <Form.Control
                    key={idx}
                    id={`${field.name}-line-${idx}`}
                    type="text"
                    value={lines[idx] || ""}
                    onChange={(e) => {
                      const newLines = [...lines];
                      newLines[idx] = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        [field.name]: newLines.join("\n"),
                      }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const isLastLine = idx === lineCount - 1;
                        if (isLastLine) {
                          // Add new line
                          const lines = (value || "").split("\n");
                          lines.push("");
                          setFormData((prev) => ({
                            ...prev,
                            [field.name]: lines.join("\n"),
                          }));
                          // Focus new line after render
                          setTimeout(() => {
                            const nextInput = document.getElementById(
                              `${field.name}-line-${idx + 1}`
                            );
                            nextInput?.focus();
                          }, 10);
                        } else {
                          // Focus next existing line
                          const nextInput = document.getElementById(
                            `${field.name}-line-${idx + 1}`
                          );
                          nextInput?.focus();
                        }
                      } else if (
                        e.key === "Backspace" &&
                        lines[idx] === "" &&
                        idx >= 3
                      ) {
                        // Remove empty line (only for lines beyond initial 3)
                        e.preventDefault();
                        const newLines = [...lines];
                        newLines.splice(idx, 1);
                        setFormData((prev) => ({
                          ...prev,
                          [field.name]: newLines.join("\n"),
                        }));
                        // Focus previous line
                        setTimeout(() => {
                          const prevInput = document.getElementById(
                            `${field.name}-line-${idx - 1}`
                          );
                          prevInput?.focus();
                        }, 10);
                      }
                    }}
                    className="rounded-0 small border-0 border-bottom"
                    style={{ boxShadow: "none" }}
                    disabled={readOnly}
                  />
                );
              })}
            </div>
          </div>
        </div>
      );

    case "file":
      const selectedFile = formData[field.name];
      const isBase64Image =
        typeof selectedFile === "string" && selectedFile.startsWith("data:image");

      if (readOnly && isBase64Image) {
        return (
          <a
            href="#"
            className="text-primary text-decoration-none small"
            onClick={(e) => {
              e.preventDefault();
              onShowImagePreview?.(selectedFile);
            }}
          >
            <i className="bi bi-image me-1"></i>View Image
          </a>
        );
      }

      if (readOnly && !selectedFile) {
        return <span className="text-muted small">No image</span>;
      }

      return (
        <div>
          <div className="d-flex align-items-center gap-2">
            <Form.Control
              type="file"
              accept={field.accept}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFileChange(e, field.name)
              }
              className="small"
              disabled={readOnly}
            />
            {selectedFile && !readOnly && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleFileClear(field.name)}
                className="small px-1"
              >
                <i className="bi bi-x"></i> Clear
              </Button>
            )}
          </div>
          {selectedFile && isBase64Image && !readOnly && (
            <div className="mt-2">
              <img src={selectedFile} alt="Preview" style={{ maxWidth: "120px", maxHeight: "120px", objectFit: "cover", border: "1px solid #dee2e6", borderRadius: "4px" }} />
            </div>
          )}
          {field.hint && (
            <Form.Text className="small text-muted d-block">
              {field.hint}
            </Form.Text>
          )}
        </div>
      );

    case "currency":
      return (
        <InputGroup>
          <InputGroup.Text className="input-icon">
            {field.prefix || "£"}
          </InputGroup.Text>
          <Form.Control
            type="number"
            name={field.name}
            value={value}
            onChange={handleInputChange}
            className={(field.readOnly || readOnly) ? "small bg-light" : "small"}
            step="0.01"
            readOnly={field.readOnly}
            disabled={readOnly}
          />
        </InputGroup>
      );

    case "tel":
      return (
        <InputGroup>
          {field.icon && (
            <InputGroup.Text className="input-icon">
              <i className={`bi ${field.icon}`}></i>
            </InputGroup.Text>
          )}
          <Form.Control
            type="tel"
            name={field.name}
            value={value}
            onChange={handleInputChange}
            className="small"
            disabled={readOnly}
          />
        </InputGroup>
      );

    case "quantity":
      return (
        <InputGroup style={{ width: "150px" }}>
          {!readOnly && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleQuantityChange(field.name, -1)}
              className="px-2"
            >
              <i className="bi bi-dash"></i>
            </Button>
          )}
          <Form.Control
            type="number"
            name={field.name}
            value={value}
            onChange={handleInputChange}
            className="small text-center"
            style={{ maxWidth: readOnly ? "100%" : "60px" }}
            min={1}
            disabled={readOnly}
          />
          {!readOnly && (
            <Button
              variant="success"
              size="sm"
              onClick={() => handleQuantityChange(field.name, 1)}
              className="px-2"
            >
              <i className="bi bi-plus"></i>
            </Button>
          )}
        </InputGroup>
      );

    case "checkbox":
      return (
        <Form.Check
          type="checkbox"
          name={field.name}
          checked={!!value}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, [field.name]: e.target.checked }))
          }
          className="mt-1"
          disabled={readOnly}
        />
      );

    case "tiers":
      const tiersData =
        formData[field.name] ||
        Array.from({ length: field.rows || 8 }, () => ({
          size: "",
          message: "",
        }));
      return (
        <div className="w-100">
          <table className="tier-table small">
            <thead>
              <tr>
                <th className="col-1">Tier</th>
                <th className="col-2">Size</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {tiersData.map((tier: any, index: number) => (
                <tr key={index}>
                  <td className="tier-number">{index + 1}</td>
                  <td>
                    <Form.Control
                      type="text"
                      value={tier.size}
                      onChange={(e) => {
                        const newTiers = [...tiersData];
                        newTiers[index] = {
                          ...newTiers[index],
                          size: e.target.value,
                        };
                        setFormData((prev) => ({
                          ...prev,
                          [field.name]: newTiers,
                        }));
                      }}
                      size="sm"
                      disabled={readOnly}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      value={tier.message}
                      onChange={(e) => {
                        const newTiers = [...tiersData];
                        newTiers[index] = {
                          ...newTiers[index],
                          message: e.target.value,
                        };
                        setFormData((prev) => ({
                          ...prev,
                          [field.name]: newTiers,
                        }));
                      }}
                      size="sm"
                      disabled={readOnly}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case "productList":
      const products = formData[field.name] || [];
      const showActions = !readOnly || supplierEditMode;
      const showAddButton = !readOnly && !supplierEditMode;

      const handleProductQuantityChange = (idx: number, newQty: number) => {
        const updatedProducts = [...products];
        updatedProducts[idx] = { ...updatedProducts[idx], quantity: Math.max(1, newQty) };
        setFormData((prev) => ({ ...prev, [field.name]: updatedProducts }));
      };

      return (
        <div className="w-100">
          <Table bordered size="sm" className="small">
            <thead>
              <tr>
                <th>Code</th>
                <th>Product</th>
                <th>Size</th>
                <th>Quantity</th>
                {showActions && (
                  <th className="col-1 text-center">
                    {showAddButton && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={onShowProductModal}
                        className="py-0 px-2"
                        title="Add Product"
                      >
                        <i className="bi bi-plus-lg"></i>
                      </Button>
                    )}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={showActions ? 5 : 4} className="text-center text-muted">
                    No products added yet
                  </td>
                </tr>
              ) : (
                products.map((p: any, idx: number) => (
                  <tr key={idx}>
                    <td className="text-code">{p.code}</td>
                    <td className="text-product">{p.name}</td>
                    <td>{p.size}</td>
                    <td>
                      {supplierEditMode || !readOnly ? (
                        <InputGroup size="sm" style={{ width: "120px" }}>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="px-2"
                            onClick={() => handleProductQuantityChange(idx, (parseInt(p.quantity) || 1) - 1)}
                          >
                            <i className="bi bi-dash"></i>
                          </Button>
                          <Form.Control
                            type="number"
                            value={p.quantity}
                            onChange={(e) => handleProductQuantityChange(idx, parseInt(e.target.value) || 1)}
                            className="text-center px-1"
                            min={1}
                            style={{ maxWidth: "50px" }}
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="px-2"
                            onClick={() => handleProductQuantityChange(idx, (parseInt(p.quantity) || 1) + 1)}
                          >
                            <i className="bi bi-plus"></i>
                          </Button>
                        </InputGroup>
                      ) : (
                        p.quantity
                      )}
                    </td>
                    {showActions && (
                      <td>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => removeProduct(field.name, idx)}
                          className="small px-1"
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>

        </div>
      );

    case "inlineProductList":
      return (
        <InlineProductListInput
          field={field}
          value={value}
          setFormData={setFormData}
          availableProducts={availableProducts}
          readOnly={readOnly}
        />
      );

    case "text":
    default:
      return (
        <InputGroup>
          {field.icon && (
            <InputGroup.Text className="input-icon">
              <i className={`bi ${field.icon}`}></i>
            </InputGroup.Text>
          )}
          <Form.Control
            type="text"
            name={field.name}
            value={value}
            onChange={handleInputChange}
            className="small"
            placeholder={field.placeholder}
            disabled={readOnly}
          />
        </InputGroup>
      );
  }
};

export default FieldRenderer;
